/**
 * The navigation bridge the remote Apps use instead of opening a new tab.
 *
 * Every App is loaded into THIS document by Module Federation, so a link from one App to
 * another App's route (`/pms`, `/class`, …) is not an external link at all — it is a route
 * of the page the code is already running in. In a desktop browser that distinction is
 * cosmetic: `window.open` puts it in a second tab and everything works.
 *
 * Inside the SIYA mobile app it is not cosmetic. There the shell is rendered in an iframe
 * inside the app's WebView (see utils/embedSignal), and a WebView has no tabs:
 *
 *   - Android's WebView is created with `setSupportMultipleWindows(false)` unless the app
 *     opts in, and then `window.open()` does not open anything — the WebView either drops
 *     the call or loads the URL over the TOP-LEVEL frame, throwing away the native Micro
 *     Apps view around it.
 *   - The `window.location.href` fallback that follows is no better from inside the frame:
 *     it replaces the shell document, so the session handover the app put on the iframe's
 *     src is gone and the app's own listener sees the frame leave.
 *
 * Either way the user is thrown out of Micro Apps, which is exactly the reported bug: the
 * ↗ arrow on an overview card exits the Micro Apps window instead of opening the module.
 *
 * So the shell publishes what it alone knows — whether this runtime has tabs at all, and
 * how to change route without a document load — and the Apps ask before they reach for
 * `window.open`. Nothing here changes desktop behaviour: `canOpenNewTab` is true in a real
 * browser and the callers open a tab exactly as before.
 */

/**
 * Fired on `window` after every completed shell route change — whichever caused it: this
 * bridge, a sidebar link, or the back button.
 *
 * A remote's page component is NOT remounted by a client-side route change, so a page that
 * reads `window.location.search` on mount alone would miss a navigation into its own route
 * with a different query (`/overview?overviewId=…` from `/overview`). Under `window.open`
 * that case never arose — it was always a fresh document. This event is how a remote
 * notices. Deliberately a DOM event rather than a shared router: it does not require the
 * remote and the shell to resolve `next/router` to the same module instance.
 */
export const SHELL_NAVIGATED_EVENT = 'siya:shell-navigated';

/** Global contract with the remotes. Versioned: a remote built against v1 must be able to
 *  tell that what it found is the shape it expects. Do not change v1 in place — add v2. */
export interface SiyaShellBridge {
  version: 1;
  /** Whether this runtime can actually open a second tab. False in every WebView. */
  canOpenNewTab: boolean;
  /**
   * Route to a shell path in place, with no document load. Returns false — and does
   * nothing — when the URL is not one of this shell's own routes, so the caller can fall
   * back to its normal external-link handling rather than silently swallowing the click.
   */
  navigate(url: string): boolean;
}

declare global {
  interface Window {
    siyaShell?: SiyaShellBridge;
  }
}

/**
 * True when this document is inside a WebView rather than a browser tab.
 *
 * Deliberately conservative in both directions. A false positive costs an in-place route
 * change where a new tab was possible — the user still lands on the right page. A false
 * negative is the bug above. Every check is a positive identification of a WebView; the
 * default is "this is a browser".
 */
export function isWebViewRuntime(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;

  // Tauri (the native SIYA shell on Mac and Android). Its own WebView, never a tab.
  const w = window as unknown as Record<string, unknown>;
  if (w.__TAURI__ || w.__TAURI_INTERNALS__) return true;

  const ua = navigator.userAgent || '';

  // Android System WebView — the runtime behind the SIYA Android app, React Native's
  // WebView and Tauri on Android alike — stamps the `wv` token into its UA.
  if (/\bwv\b/.test(ua)) return true;
  // Older/vendor Android WebViews omit `wv` but keep the `Version/x.y` token that Chrome
  // for Android does not send.
  if (/Android/.test(ua) && /Version\/\d+\.\d+/.test(ua)) return true;

  // On iOS every browser is WKWebView, so the UA cannot say "is this WebKit" — it can only
  // say whether this is Safari or an app hosting the engine itself. Safari (and Chrome and
  // Firefox for iOS, which are Safari with a prefix) all send the `Safari/` token; an
  // in-app WKWebView sends `Mobile/…` and stops.
  if (/\b(iPhone|iPad|iPod)\b/.test(ua) && !/Safari\//.test(ua)) return true;

  return false;
}

/**
 * Install the bridge. `navigate` comes from the caller so this file never imports the
 * router: the shell has exactly one router instance and it belongs to _app.
 *
 * Returns the teardown, and removes only a bridge it still owns — a StrictMode double
 * mount must not leave the window without one.
 */
export function installShellBridge(navigate: (path: string) => void): () => void {
  if (typeof window === 'undefined') return () => {};

  const bridge: SiyaShellBridge = {
    version: 1,
    canOpenNewTab: !isWebViewRuntime(),
    navigate(url: string): boolean {
      try {
        // Relative or absolute, both are the App's to send; resolve against this document
        // so a same-origin absolute URL (which is what the Apps build from their own
        // NEXT_PUBLIC_BASE_URL) is recognised as ours.
        const target = new URL(url, window.location.href);
        // Cross-origin is not ours to route — say so and let the caller open it properly.
        // This is also what makes a stale/mismatched NEXT_PUBLIC_BASE_URL in a remote fail
        // safe instead of routing to a path that does not exist here.
        if (target.origin !== window.location.origin) return false;
        navigate(target.pathname + target.search + target.hash);
        return true;
      } catch {
        return false;
      }
    },
  };

  window.siyaShell = bridge;
  return () => {
    if (window.siyaShell === bridge) delete window.siyaShell;
  };
}
